import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { Alert, inputClass } from '../partner-network/ui';

const PartnerLogin: React.FC = () => {
  const { signIn } = usePartnerAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Where the guard bounced them from, so they land back on it. */
  const from = (location.state as { from?: string } | null)?.from || '/partner/dashboard';

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }

    try {
      setSubmitting(true);
      await signIn(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in');
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="flex min-h-screen items-center justify-center bg-surface px-4 py-20">
      <AnimatedSection animation="slide-up" className="w-full max-w-md">
        <div className="rounded-2xl border border-surface-border bg-surface-card p-8">
          <img src="/nexalogo.png" alt="Nexasolutra" className="mb-6 h-11 w-11 object-contain" />

          <h1 className="mb-2 font-display text-2xl font-semibold text-white">
            Partner sign in
          </h1>
          <p className="mb-8 text-sm text-muted">
            Submit referrals and track your commissions.
          </p>

          {error && (
            <Alert tone="error" className="mb-5">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-2 block text-sm text-muted">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                autoComplete="username"
                autoFocus
                className={inputClass}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm text-muted">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className={inputClass}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-muted">
            Not a partner yet?{' '}
            <Link to="/partner/register" className="text-primary-400 hover:underline">
              Apply to join
            </Link>
          </p>
        </div>
      </AnimatedSection>
    </section>
  );
};

export default PartnerLogin;
